import type { ApiErrorBody } from '@rw/shared';
import { t } from '../i18n';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly details?: unknown,
  ) {
    // Server messages are English; show the translation when the dictionary has one.
    super(t(message));
    this.name = 'ApiError';
  }

  /** Network failure, timeout or server unavailable — worth retrying later. */
  get isTransient() {
    return this.status === 0 || this.status === 408 || this.status === 429 || this.status >= 500;
  }
}

type TokenGetter = () => string | null;
type UnauthorizedHandler = () => void;
type ReachabilityHandler = (reachable: boolean) => void;

let getToken: TokenGetter = () => null;
let onUnauthorized: UnauthorizedHandler = () => {};
let onReachability: ReachabilityHandler = () => {};

export function configureApi(opts: {
  getToken: TokenGetter;
  onUnauthorized: UnauthorizedHandler;
  /** Called after every request: is the API server answering? */
  onReachability?: ReachabilityHandler;
}) {
  getToken = opts.getToken;
  onUnauthorized = opts.onUnauthorized;
  onReachability = opts.onReachability ?? (() => {});
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Multipart upload (sent as-is instead of JSON). */
  form?: FormData;
  query?: Record<string, string | number | undefined>;
  timeoutMs?: number;
  signal?: AbortSignal;
  /** Let the request outlive the page (e.g. sent while it is being hidden or closed). */
  keepalive?: boolean;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(`/api${path}`, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) if (v !== undefined) url.searchParams.set(k, String(v));
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (opts.body !== undefined && !opts.form) headers['Content-Type'] = 'application/json';

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), opts.timeoutMs ?? 15_000);
  opts.signal?.addEventListener('abort', () => controller.abort());
  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? (opts.body !== undefined || opts.form ? 'POST' : 'GET'),
      headers,
      body: opts.form ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
      signal: controller.signal,
      keepalive: opts.keepalive,
    });
  } catch (e) {
    const aborted = (e as Error).name === 'AbortError';
    if (navigator.onLine) onReachability(false);
    throw new ApiError(0, aborted ? 'Request timed out' : 'Network unavailable', aborted ? 'TIMEOUT' : 'NETWORK');
  } finally {
    clearTimeout(timeout);
  }

  // 502/503/504 come from the proxy/tunnel when the API itself is down.
  onReachability(res.status < 502 || res.status > 504);

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const body = (data ?? {}) as Partial<ApiErrorBody>;
    if (res.status === 401 && token) onUnauthorized();
    const message = typeof body.message === 'string' ? body.message : res.status >= 500 ? 'Server unavailable' : 'Request failed';
    throw new ApiError(res.status, message, body.code, body.details);
  }
  return data as T;
}
